import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { allQuests, artists } from "@/mocks/artistData";
import Navbar from "@/pages/home/components/Navbar";
import PromptBuilder from "@/pages/quests/create/components/PromptBuilder";
import IPImageGenerator, { GeneratedIPImage } from "@/pages/quests/create/components/IPImageGenerator";
import CollabWorkModal from "@/pages/quests/create/components/CollabWorkModal";

const colorMap: Record<string, { glow: string; bg: string; border: string; text: string }> = {
  cyan:    { glow: "#00f5d4", bg: "bg-[#00f5d4]/10", border: "border-[#00f5d4]/30", text: "text-[#00f5d4]" },
  magenta: { glow: "#f72585", bg: "bg-[#f72585]/10", border: "border-[#f72585]/30", text: "text-[#f72585]" },
  yellow:  { glow: "#ffd60a", bg: "bg-[#ffd60a]/10", border: "border-[#ffd60a]/30", text: "text-[#ffd60a]" },
  green:   { glow: "#39ff14", bg: "bg-[#39ff14]/10", border: "border-[#39ff14]/30", text: "text-[#39ff14]" },
  orange:  { glow: "#ff6b35", bg: "bg-[#ff6b35]/10", border: "border-[#ff6b35]/30", text: "text-[#ff6b35]" },
  purple:  { glow: "#b44fff", bg: "bg-[#b44fff]/10", border: "border-[#b44fff]/30", text: "text-[#b44fff]" },
};

const durationOptions = ["5초", "10초", "15초", "30초"];

type Step = 1 | 2 | 3;

export default function QuestCreatePage() {
  const { questId } = useParams<{ questId: string }>();

  const quest = allQuests.find((q) => q.id === questId);
  const artist = quest ? artists.find((a) => a.id === quest.artistId) : null;

  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [selectedTool, setSelectedTool] = useState("seedance");
  const [prompt, setPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [duration, setDuration] = useState("15초");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [videoTitle, setVideoTitle] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [ipImages, setIpImages] = useState<GeneratedIPImage[]>([]);
  const [spentTokens, setSpentTokens] = useState(0);
  const [collabModalOpen, setCollabModalOpen] = useState(false);
  const [collabAsset, setCollabAsset] = useState<{ videoTitle: string; lastFrameUrl: string; generatedImageUrl: string; creatorName: string; tokenRewarded: number } | null>(null);
  const [collabToastVisible, setCollabToastVisible] = useState(false);

  if (!quest || !artist) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/40 text-lg mb-4">캠페인을 찾을 수 없습니다.</p>
          <Link to="/quests" className="text-[#00f5d4] hover:underline cursor-pointer">캠페인 목록으로</Link>
        </div>
      </div>
    );
  }

  const c = colorMap[quest.color] ?? colorMap.cyan;

  const handleGenerate = () => {
    if (!prompt.trim()) return;
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setGenerated(true);
    }, 3000);
  };

  const handleSubmit = () => {
    if (!videoTitle.trim()) return;
    setSubmitted(true);
  };

  const handleIPComplete = (images: GeneratedIPImage[]) => {
    setIpImages(images);
    setCurrentStep(3);
  };

  const handleTokenSpend = (amount: number) => {
    setSpentTokens(prev => prev + amount);
  };

  const handleCollabSelect = (video: { id: string; user: { name: string }; title: string; lastFrameUrl: string; generatedImageUrl: string }, tokenRewarded: number) => {
    setCollabAsset({
      videoTitle: video.title,
      lastFrameUrl: video.lastFrameUrl,
      generatedImageUrl: video.generatedImageUrl,
      creatorName: video.user.name,
      tokenRewarded,
    });
    setCollabModalOpen(false);
    setCollabToastVisible(true);
    setTimeout(() => setCollabToastVisible(false), 4000);
  };

  const stepLabels = ["프롬프트 작성", "IP 이미지 생성", "영상 제출"];

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
        <Navbar />
        <div className="text-center max-w-md mx-auto px-6 pt-20">
          <div
            className="w-24 h-24 flex items-center justify-center rounded-3xl mx-auto mb-6"
            style={{ background: `linear-gradient(135deg, ${c.glow}30, ${c.glow}10)`, border: `2px solid ${c.glow}60` }}
          >
            <i className="ri-check-double-line text-5xl" style={{ color: c.glow }} />
          </div>
          <h2 className="text-3xl font-black text-white mb-3">제출 완료!</h2>
          <p className="text-white/50 text-base mb-2">
            <span className="font-bold" style={{ color: c.glow }}>{quest.reward.toLocaleString()} {quest.rewardType}</span>가 지급됩니다.
          </p>
          <p className="text-white/30 text-sm mb-10">커뮤니티 피드에 영상이 공유되었습니다.</p>
          <div className="flex flex-col gap-3">
            <Link
              to="/community"
              className="w-full py-3 rounded-xl font-bold text-white text-center cursor-pointer whitespace-nowrap"
              style={{ background: `linear-gradient(135deg, ${c.glow}, ${artist.secondaryColor})` }}
            >
              커뮤니티에서 확인하기
            </Link>
            <Link
              to="/quests"
              className="w-full py-3 rounded-xl font-bold text-white/60 text-center border border-white/15 hover:border-white/30 hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              다른 캠페인 도전하기
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      {/* Top Bar */}
      <div className="sticky top-16 z-40 bg-[#0a0a0f]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-white/40 min-w-0">
            <Link to="/quests" className="hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap">캠페인</Link>
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-arrow-right-s-line text-xs" /></div>
            <Link to={`/quests/${quest.id}`} className="hover:text-white/70 transition-colors cursor-pointer truncate">{quest.title}</Link>
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-arrow-right-s-line text-xs" /></div>
            <span className="text-white/70 whitespace-nowrap">영상 제작</span>
          </div>

          <div className="hidden sm:flex items-center gap-2">
            {stepLabels.map((label, i) => {
              const stepNum = (i + 1) as Step;
              const isActive = currentStep === stepNum;
              const isDone = currentStep > stepNum;
              return (
                <div key={label} className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold transition-all"
                      style={
                        isDone
                          ? { background: `${c.glow}30`, color: c.glow, border: `1px solid ${c.glow}60` }
                          : isActive
                          ? { background: c.glow, color: "#0a0a0f" }
                          : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.3)", border: "1px solid rgba(255,255,255,0.1)" }
                      }
                    >
                      {isDone ? <i className="ri-check-line text-xs" /> : stepNum}
                    </div>
                    <span className={`text-xs font-medium whitespace-nowrap ${isActive ? "text-white" : isDone ? "text-white/50" : "text-white/25"}`}>
                      {label}
                    </span>
                  </div>
                  {i < stepLabels.length - 1 && <div className="w-6 h-px bg-white/10" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-16 pb-24">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Main Content */}
          <div className="flex-1 space-y-8">

            {/* Campaign Overview Card */}
            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: `${c.glow}25` }}>
              {/* Thumbnail Hero */}
              <div className="relative h-40 overflow-hidden">
                <img src={quest.thumbnail} alt={quest.title} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 20%, rgba(10,10,15,0.85) 100%)` }} />
                <div className="absolute inset-0" style={{ background: `${c.glow}10` }} />
                {/* Badges */}
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span className="px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: `${c.glow}25`, color: c.glow, border: `1px solid ${c.glow}40` }}>
                    {quest.tag}
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-white/10 text-white/70 border border-white/15">
                    {quest.difficulty}
                  </span>
                </div>
                {/* Reward badge */}
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{ background: `${c.glow}20`, border: `1px solid ${c.glow}40` }}>
                  <i className="ri-coin-fill text-sm" style={{ color: c.glow }} />
                  <span className="font-black text-sm" style={{ color: c.glow }}>{quest.reward.toLocaleString()}</span>
                  <span className="text-white/50 text-xs">{quest.rewardType}</span>
                </div>
                {/* Artist + Title */}
                <div className="absolute bottom-3 left-4 right-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border border-white/20">
                      <img src={artist.avatar} alt={artist.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs font-semibold" style={{ color: artist.accentColor }}>{artist.name}</span>
                    {artist.verified && <i className="ri-verified-badge-fill text-xs" style={{ color: artist.accentColor }} />}
                  </div>
                  <h2 className="text-white font-black text-lg leading-tight">{quest.title}</h2>
                </div>
              </div>

              {/* Content Body */}
              <div className="p-5 space-y-5" style={{ background: `${c.glow}05` }}>

                {/* Campaign Purpose */}
                <div>
                  <div className="flex items-center gap-2 mb-2.5">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-megaphone-line text-sm" style={{ color: c.glow }} />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: c.glow }}>캠페인 소개</span>
                  </div>
                  <p className="text-white/65 text-sm leading-relaxed">{quest.description}</p>
                </div>

                <div className="h-px bg-white/8" />

                {/* Why this campaign */}
                <div>
                  <div className="flex items-center gap-2 mb-2.5">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-question-answer-line text-sm" style={{ color: c.glow }} />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: c.glow }}>왜 이 캠페인인가요?</span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed">
                    {artist.name}은(는) 팬들이 직접 창작에 참여하는 새로운 팬덤 문화를 만들어가고 있어요.
                    AI 기술을 통해 누구나 전문 영상 크리에이터가 될 수 있고, 팬의 상상력이 실제 콘텐츠로 탄생하는 경험을 제공합니다.
                    이 캠페인은 단순한 팬 활동을 넘어, 아티스트와 팬이 함께 만드는 공식 IP 콘텐츠 프로젝트예요.
                  </p>
                </div>

                <div className="h-px bg-white/8" />

                {/* What we do with fan videos */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-film-line text-sm" style={{ color: c.glow }} />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: c.glow }}>팬 영상 활용 계획</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {[
                      { icon: "ri-tv-2-line", title: "공식 채널 큐레이션", desc: "우수작은 아티스트 공식 SNS에 소개됩니다" },
                      { icon: "ri-trophy-line", title: "베스트 크리에이터 선정", desc: "매월 TOP 3 크리에이터에게 특별 보상 지급" },
                      { icon: "ri-archive-line", title: "IP 아카이브 등재", desc: "팬 창작물이 공식 IP 아카이브에 영구 보존" },
                    ].map((item, i) => (
                      <div key={i} className="rounded-xl p-3 border border-white/8 bg-white/3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg mb-2" style={{ background: `${c.glow}15` }}>
                          <i className={`${item.icon} text-sm`} style={{ color: c.glow }} />
                        </div>
                        <p className="text-white/80 text-xs font-semibold mb-1">{item.title}</p>
                        <p className="text-white/35 text-[11px] leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="h-px bg-white/8" />

                {/* Guidelines */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-list-check-3 text-sm" style={{ color: c.glow }} />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: c.glow }}>제작 가이드라인</span>
                  </div>
                  <div className="space-y-2">
                    {[
                      { ok: true,  text: "IP 태그(@IP1, @IP2)를 활용해 아티스트 캐릭터를 영상에 포함하세요" },
                      { ok: true,  text: "캠페인 힌트에 제시된 분위기와 설정을 최대한 반영해주세요" },
                      { ok: true,  text: "권장 AI 도구와 프리셋을 활용하면 더 높은 퀄리티를 얻을 수 있어요" },
                      { ok: false, text: "타 아티스트 IP나 저작권 보호 콘텐츠를 무단으로 사용하지 마세요" },
                      { ok: false, text: "폭력적·선정적 표현이나 아티스트 이미지를 훼손하는 내용은 제출 불가" },
                    ].map((g, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 rounded-full"
                          style={g.ok ? { background: `${c.glow}20` } : { background: "rgba(255,80,80,0.15)" }}>
                          <i className={`${g.ok ? "ri-check-line" : "ri-close-line"} text-[10px]`}
                            style={{ color: g.ok ? c.glow : "#ff5050" }} />
                        </div>
                        <p className="text-white/50 text-xs leading-relaxed">{g.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stats row */}
                <div className="flex items-center gap-4 pt-1">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-group-line text-xs text-white/30" />
                    </div>
                    <span className="text-white/40 text-xs">{quest.participants.toLocaleString()}명 참여 중</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line text-xs text-white/30" />
                    </div>
                    <span className="text-white/40 text-xs">{quest.deadline}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-bar-chart-line text-xs text-white/30" />
                    </div>
                    <span className="text-white/40 text-xs">진행률 {quest.progress}%</span>
                  </div>
                </div>

                {/* Collab Work Button */}
                <div className="pt-1">
                  <button
                    onClick={() => setCollabModalOpen(true)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all duration-200 cursor-pointer group"
                    style={{
                      borderColor: `${c.glow}35`,
                      background: `${c.glow}08`,
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                        style={{ background: `${c.glow}20` }}
                      >
                        <i className="ri-links-line text-sm" style={{ color: c.glow }} />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold" style={{ color: c.glow }}>연결하여 작업</p>
                        <p className="text-white/35 text-[11px] mt-px">
                          다른 창작자 영상의 마지막 장면 · 생성 이미지를 asset으로 활용
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold"
                        style={{ background: `${c.glow}20`, color: c.glow }}
                      >
                        <i className="ri-coin-fill text-xs" />
                        토큰 지급
                      </span>
                      <i className="ri-arrow-right-s-line text-white/30 group-hover:text-white/60 transition-colors" />
                    </div>
                  </button>

                  {/* Collab asset applied badge */}
                  {collabAsset && (
                    <div
                      className="mt-2 flex items-center gap-2.5 px-3 py-2.5 rounded-xl border"
                      style={{ borderColor: `${c.glow}30`, background: `${c.glow}08` }}
                    >
                      <div className="flex gap-1.5 flex-shrink-0">
                        <div className="w-8 h-8 rounded-lg overflow-hidden border border-white/15">
                          <img src={collabAsset.lastFrameUrl} alt="last frame" className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="w-8 h-8 rounded-lg overflow-hidden border border-white/15">
                          <img src={collabAsset.generatedImageUrl} alt="generated" className="w-full h-full object-cover object-top" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold" style={{ color: c.glow }}>
                          <i className="ri-check-double-line mr-1" />
                          Asset 적용됨
                        </p>
                        <p className="text-white/35 text-[11px] truncate">
                          {collabAsset.creatorName}의 영상 · {collabAsset.tokenRewarded} 토큰 지급 완료
                        </p>
                      </div>
                      <button
                        onClick={() => setCollabAsset(null)}
                        className="w-5 h-5 flex items-center justify-center rounded-md border border-white/10 text-white/30 hover:text-white/60 transition-colors cursor-pointer flex-shrink-0"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* STEP 1: 프롬프트 작성 */}
            <section className={`rounded-2xl border p-6 transition-all duration-300 ${currentStep === 1 ? "border-white/20 bg-white/5" : "border-white/8 bg-white/3"}`}>
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold"
                  style={currentStep > 1 ? { background: `${c.glow}20`, color: c.glow } : { background: c.glow, color: "#0a0a0f" }}
                >
                  {currentStep > 1 ? <i className="ri-check-line" /> : "1"}
                </div>
                <h3 className="text-white font-bold text-base">프롬프트 작성</h3>
                {currentStep > 1 && (
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="ml-auto text-xs text-white/30 hover:text-white/60 transition-colors cursor-pointer"
                  >
                    수정하기
                  </button>
                )}
              </div>
              {currentStep === 1 && (
                <>
                  <PromptBuilder
                    quest={quest}
                    artist={artist}
                    accentColor={c.glow}
                    secondaryColor={artist.secondaryColor}
                    prompt={prompt}
                    onPromptChange={setPrompt}
                    negativePrompt={negativePrompt}
                    onNegativePromptChange={setNegativePrompt}
                    selectedTool={selectedTool}
                    onToolChange={setSelectedTool}
                  />

                  {/* Duration */}
                  <div className="mt-5">
                    <label className="text-white/60 text-xs font-semibold mb-2 block">영상 길이</label>
                    <div className="flex gap-2 flex-wrap">
                      {durationOptions.map((d) => (
                        <button
                          key={d}
                          onClick={() => setDuration(d)}
                          className="px-3 py-1.5 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                          style={duration === d ? { borderColor: `${c.glow}60`, background: `${c.glow}15`, color: c.glow } : { borderColor: "rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.40)" }}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Next Step Button */}
                  <div className="mt-6">
                    <button
                      onClick={() => setCurrentStep(2)}
                      disabled={!prompt.trim()}
                      className="w-full py-4 rounded-xl font-bold text-base text-white transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: `linear-gradient(135deg, ${c.glow}, ${artist.secondaryColor})` }}
                    >
                      <i className="ri-video-ai-line text-xl" />
                      영상 생성하기
                      <i className="ri-arrow-right-line text-base" />
                    </button>
                  </div>
                </>
              )}
              {currentStep > 1 && prompt && (
                <div className="rounded-xl p-3" style={{ background: `${c.glow}06`, border: `1px solid ${c.glow}15` }}>
                  <p className="text-white/40 text-xs font-mono leading-relaxed line-clamp-2">{prompt}</p>
                </div>
              )}
            </section>

            {/* STEP 2: IP 이미지 생성 */}
            {currentStep >= 2 && (
              <section className={`rounded-2xl border p-6 transition-all duration-300 ${currentStep === 2 ? "border-white/20 bg-white/5" : "border-white/8 bg-white/3"}`}>
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold"
                    style={currentStep > 2 ? { background: `${c.glow}20`, color: c.glow } : currentStep === 2 ? { background: c.glow, color: "#0a0a0f" } : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.3)" }}
                  >
                    {currentStep > 2 ? <i className="ri-check-line" /> : "2"}
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base">IP 이미지 생성</h3>
                    <p className="text-white/30 text-xs mt-0.5">의상·배경을 바꿔 나만의 IP 이미지를 만드세요</p>
                  </div>
                  {currentStep > 2 && (
                    <button
                      onClick={() => setCurrentStep(2)}
                      className="ml-auto text-xs text-white/30 hover:text-white/60 transition-colors cursor-pointer"
                    >
                      수정하기
                    </button>
                  )}
                </div>

                {currentStep === 2 && (
                  <IPImageGenerator
                    quest={quest}
                    artist={artist}
                    accentColor={c.glow}
                    onComplete={handleIPComplete}
                    onTokenSpend={handleTokenSpend}
                  />
                )}

                {currentStep > 2 && (
                  <div className="space-y-2">
                    {ipImages.length > 0 ? (
                      <>
                        <div className="flex flex-wrap gap-2">
                          {ipImages.map(img => (
                            <div key={img.id} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 bg-white/3">
                              <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                                <img src={img.previewUrl} alt={img.ipTag} className="w-full h-full object-cover object-top" />
                              </div>
                              <div>
                                <span className="text-xs font-bold font-mono" style={{ color: c.glow }}>{img.ipTag}</span>
                                <p className="text-[10px] text-white/30">{img.outfit} · {img.background}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-white/30 text-xs">
                          <i className="ri-coin-fill mr-1" style={{ color: c.glow }} />
                          {spentTokens} 토큰 사용됨
                        </p>
                      </>
                    ) : (
                      <p className="text-white/30 text-xs">IP 이미지 없이 진행</p>
                    )}
                  </div>
                )}
              </section>
            )}

            {/* STEP 3: 영상 생성 & 제출 */}
            {currentStep >= 3 && (
              <section className={`rounded-2xl border p-6 transition-all duration-300 ${currentStep === 3 ? "border-white/20 bg-white/5" : "border-white/8 bg-white/3"}`}>
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold"
                    style={{ background: c.glow, color: "#0a0a0f" }}
                  >
                    3
                  </div>
                  <h3 className="text-white font-bold text-base">영상 생성 &amp; 제출</h3>
                </div>

                {/* IP 이미지 참조 표시 */}
                {ipImages.length > 0 && (
                  <div className="rounded-xl p-3 mb-5 border border-white/8 bg-white/3">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-image-ai-line text-xs" style={{ color: c.glow }} />
                      <span className="text-xs font-semibold" style={{ color: c.glow }}>참조 IP 이미지 적용됨</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {ipImages.map(img => (
                        <div key={img.id} className="flex items-center gap-1.5">
                          <div className="w-6 h-6 rounded-md overflow-hidden border border-white/10">
                            <img src={img.previewUrl} alt={img.ipTag} className="w-full h-full object-cover object-top" />
                          </div>
                          <span className="text-[11px] font-mono text-white/50">{img.ipTag}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Generate Video */}
                {!generated ? (
                  <div className="mb-5">
                    <button
                      onClick={handleGenerate}
                      disabled={isGenerating}
                      className="w-full py-4 rounded-xl font-bold text-base text-white transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: `linear-gradient(135deg, ${c.glow}, ${artist.secondaryColor})` }}
                    >
                      {isGenerating ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          AI 영상 생성 중... (약 30초 소요)
                        </>
                      ) : (
                        <>
                          <i className="ri-magic-line text-xl" />
                          AI 영상 생성하기
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="rounded-xl overflow-hidden border border-white/10 mb-5" style={{ aspectRatio: "16/9" }}>
                      <div className="w-full h-full relative">
                        <img src={quest.thumbnail} alt="Generated Video Preview" className="w-full h-full object-cover object-top" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <div className="text-center">
                            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30 mx-auto mb-3">
                              <i className="ri-play-fill text-white text-2xl" />
                            </div>
                            <span className="text-white/60 text-sm">생성된 AI 영상 미리보기</span>
                          </div>
                        </div>
                        <div className="absolute top-3 right-3 px-3 py-1 rounded-full" style={{ background: `${c.glow}20`, border: `1px solid ${c.glow}40` }}>
                          <span className="text-xs font-semibold" style={{ color: c.glow }}>AI 생성 완료</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-5">
                      <label className="text-white/60 text-xs font-semibold mb-2 block">영상 제목 *</label>
                      <input
                        type="text"
                        value={videoTitle}
                        onChange={(e) => setVideoTitle(e.target.value)}
                        placeholder="커뮤니티에 표시될 영상 제목을 입력하세요"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-white/30 transition-colors"
                        maxLength={60}
                      />
                    </div>

                    <div className="rounded-xl p-4 mb-5" style={{ background: `${c.glow}08`, border: `1px solid ${c.glow}20` }}>
                      <div className="flex items-center gap-3">
                        <i className="ri-information-line text-lg" style={{ color: c.glow }} />
                        <div>
                          <p className="text-white/70 text-sm">제출 시 커뮤니티 피드에 공개되며</p>
                          <p className="text-sm font-bold" style={{ color: c.glow }}>{quest.reward.toLocaleString()} {quest.rewardType}가 즉시 지급됩니다.</p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={handleSubmit}
                      disabled={!videoTitle.trim()}
                      className="w-full py-4 rounded-xl font-bold text-base text-white transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: `linear-gradient(135deg, ${c.glow}, ${artist.secondaryColor})` }}
                    >
                      <i className="ri-upload-cloud-line text-xl" />
                      커뮤니티에 제출하기
                    </button>
                  </>
                )}
              </section>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="w-full lg:w-72 flex-shrink-0 space-y-5">
            {/* Quest IP Images */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-3">퀘스트 IP</h4>
              <div className="grid grid-cols-2 gap-2">
                {artist.ipImages.map((img, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-white/10" style={{ aspectRatio: "1/1" }}>
                    <img src={img} alt={`IP ${i + 1}`} className="w-full h-full object-cover object-top" />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
                  <img src={artist.avatar} alt={artist.name} className="w-full h-full object-cover" />
                </div>
                <span className="text-white/50 text-xs">{artist.name} IP 목록</span>
              </div>
            </div>

            {/* Tips */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-4">제작 팁</h4>
              <div className="flex flex-col gap-3">
                {[
                  { icon: "ri-at-line", color: "#ffd60a", tip: "@IP1, @IP2 태그로 IP 캐릭터를 프롬프트에 삽입하세요." },
                  { icon: "ri-camera-3-line", color: "#00f5d4", tip: "카메라 기법 프리셋을 활용하면 더 전문적인 영상이 나와요." },
                  { icon: "ri-time-line", color: "#f72585", tip: "15초 이하 영상이 커뮤니티에서 더 높은 참여율을 보여요." },
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${t.icon} text-sm`} style={{ color: t.color }} />
                    </div>
                    <p className="text-white/40 text-xs leading-relaxed">{t.tip}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quest Reward Summary */}
            <div className="rounded-2xl border p-5" style={{ borderColor: `${c.glow}30`, background: `${c.glow}08` }}>
              <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-3">완료 보상</h4>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: `${c.glow}20` }}>
                  <i className="ri-coin-fill text-xl" style={{ color: c.glow }} />
                </div>
                <div>
                  <div className="font-black text-2xl" style={{ color: c.glow }}>{quest.reward.toLocaleString()}</div>
                  <div className="text-white/40 text-xs">{quest.rewardType}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Collab Work Modal */}
      {collabModalOpen && (
        <CollabWorkModal
          quest={quest}
          artist={artist}
          accentColor={c.glow}
          onClose={() => setCollabModalOpen(false)}
          onSelectAsset={handleCollabSelect}
        />
      )}

      {/* Token reward toast */}
      {collabToastVisible && collabAsset && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl border shadow-lg"
          style={{
            background: "#0f0f18",
            borderColor: `${c.glow}40`,
            animation: "slideUp 0.3s ease-out",
          }}
        >
          <div
            className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
            style={{ background: `${c.glow}20` }}
          >
            <i className="ri-coin-fill text-base" style={{ color: c.glow }} />
          </div>
          <div>
            <p className="text-white/80 text-sm font-semibold">
              <span style={{ color: c.glow }}>{collabAsset.creatorName}</span>에게{" "}
              <span style={{ color: c.glow }}>{collabAsset.tokenRewarded} 토큰</span> 지급 완료!
            </p>
            <p className="text-white/35 text-xs mt-0.5">연결 작업 asset이 프롬프트에 적용되었습니다</p>
          </div>
          <i className="ri-check-double-line text-lg flex-shrink-0" style={{ color: c.glow }} />
        </div>
      )}
    </div>
  );
}
